import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Send, Loader2, X, CornerDownRight, ImagePlus } from 'lucide-react';

const MENTION_RE = /(^|\s)@([^\s@]*)$/;

// Shared composer: bottom (new message), inline reply, inline edit
export const Composer = ({
  members = [], onSubmit, onCancel, onTyping, onUploadImage = null, everyoneLabel = null, initialValue = '', initialMentions = [],
  placeholder = 'Type a message…', submitLabel, autoFocus = false, replyPreview = null, compact = false, testId = 'chat-input',
  allowMentions = true, maxLength = 500
}) => {
  const [value, setValue] = useState(initialValue);
  const [picked, setPicked] = useState(initialMentions);
  const [sending, setSending] = useState(false);
  const [popIndex, setPopIndex] = useState(0);
  const [image, setImage] = useState(null); // { file, preview }
  const ref = useRef(null);
  const fileRef = useRef(null);
  const lastTypingRef = useRef(0);

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 15 * 1024 * 1024) { alert('Image too large (max 15MB)'); return; }
    if (image?.preview) URL.revokeObjectURL(image.preview);
    setImage({ file, preview: URL.createObjectURL(file) });
    ref.current?.focus();
  };
  const clearImage = () => { if (image?.preview) URL.revokeObjectURL(image.preview); setImage(null); };

  const handleChange = (e) => {
    setValue(e.target.value);
    if (onTyping && e.target.value.trim() && Date.now() - lastTypingRef.current > 2500) {
      lastTypingRef.current = Date.now();
      onTyping();
    }
  };

  useEffect(() => {
    if (autoFocus && ref.current) { ref.current.focus(); ref.current.setSelectionRange(value.length, value.length); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  useEffect(() => {
    const el = ref.current; if (!el) return;
    el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [value]);

  const caretText = () => value.slice(0, ref.current?.selectionStart ?? value.length);
  const mentionMatch = MENTION_RE.exec(caretText());
  const suggestions = useMemo(() => {
    if (!mentionMatch || !allowMentions) return [];
    const q = mentionMatch[2].toLowerCase();
    const name = (m) => (m.displayName || '').toLowerCase();
    const list = members.filter((m) => name(m).includes(q))
      .sort((a, b) => (name(a).startsWith(q) ? 0 : 1) - (name(b).startsWith(q) ? 0 : 1) || name(a).localeCompare(name(b)))
      .slice(0, 6);
    if (everyoneLabel && 'everyone'.startsWith(q)) list.unshift({ id: '*', displayName: 'everyone', everyone: true, hint: everyoneLabel });
    return list;
  }, [mentionMatch, members, everyoneLabel, allowMentions]);

  useEffect(() => { setPopIndex(0); }, [suggestions.length]);

  const pickMember = (m) => {
    const before = caretText().replace(MENTION_RE, `$1@${m.displayName} `);
    const after = value.slice(ref.current?.selectionStart ?? value.length);
    setValue(before + after);
    if (!m.everyone) setPicked((p) => (p.some((x) => x.id === m.id) ? p : [...p, { id: m.id, displayName: m.displayName }]));
    requestAnimationFrame(() => { ref.current?.focus(); ref.current?.setSelectionRange(before.length, before.length); });
  };

  const submit = async () => {
    const text = value.trim();
    if ((!text && !image) || sending) return;
    const mentionIds = picked.filter((p) => text.includes(`@${p.displayName}`)).map((p) => p.id);
    try {
      setSending(true);
      let uploaded = null;
      if (image && onUploadImage) uploaded = await onUploadImage(image.file);
      await onSubmit(text, mentionIds, uploaded);
      setValue(''); setPicked([]); clearImage();
    }
    finally { setSending(false); }
  };

  const onKeyDown = (e) => {
    if (suggestions.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setPopIndex((i) => (i + 1) % suggestions.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setPopIndex((i) => (i - 1 + suggestions.length) % suggestions.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMember(suggestions[popIndex]); return; }
    }
    if (e.key === 'Escape' && onCancel) { e.preventDefault(); onCancel(); return; }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  return (
    <div className="relative">
      {suggestions.length > 0 && (
        <div className="absolute bottom-full mb-2 left-0 w-72 bg-[#18181b] border border-zinc-700 rounded-lg shadow-xl max-h-56 overflow-y-auto z-50 p-1 flex flex-col gap-0.5" data-testid="mention-popover">
          {suggestions.map((m, i) => (
            <button key={m.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickMember(m)}
              className={`flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs ${i === popIndex ? 'bg-amber-600/20 text-amber-300' : 'text-zinc-200 hover:bg-zinc-800'}`}
              data-testid={m.everyone ? 'mention-option-everyone' : `mention-option-${m.id}`}>
              {m.everyone ? (
                <span className="w-5 h-5 rounded-full bg-amber-600/30 border border-amber-500/50 flex items-center justify-center text-[10px] text-amber-400 font-bold">@</span>
              ) : (
                <Avatar className="w-5 h-5"><AvatarImage src={m.avatarUrl} /><AvatarFallback className="bg-zinc-800 text-[10px] text-amber-500">{m.displayName?.charAt(0)}</AvatarFallback></Avatar>
              )}
              <span className="truncate">{m.displayName}</span>
              {m.everyone && <span className="ml-auto text-[9px] text-zinc-500 font-typewriter truncate">{m.hint}</span>}
              {m.role === 'owner' && <span className="ml-auto text-[9px] text-red-500 font-headline tracking-widest">OWNER</span>}
            </button>
          ))}
        </div>
      )}
      {replyPreview && (
        <div className="flex items-center gap-2 mb-1.5 border-l-2 border-amber-500/60 pl-2 py-1 text-xs text-zinc-400 font-typewriter bg-zinc-900/50 rounded-r" data-testid="reply-preview">
          <CornerDownRight className="w-3 h-3 text-amber-500 flex-shrink-0" />
          <span className="text-amber-500/90">{replyPreview.senderName}</span>
          <span className="truncate flex-1">{replyPreview.content}</span>
        </div>
      )}
      {image && (
        <div className="relative inline-block mb-2" data-testid="composer-image-preview">
          <img src={image.preview} alt="" className="h-20 w-auto rounded-md border border-zinc-700 object-cover" />
          <button type="button" onClick={clearImage} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-zinc-900 border border-zinc-600 text-zinc-300 hover:text-red-400 flex items-center justify-center" data-testid="composer-image-remove"><X className="w-3 h-3" /></button>
        </div>
      )}
      <div className="flex items-end gap-2">
        {onUploadImage && (
          <>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="chat-image-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={sending} className="h-9 w-9 flex items-center justify-center rounded-lg text-zinc-400 hover:text-amber-400 hover:bg-zinc-800 transition-colors" title="Attach a photo" data-testid="chat-image-btn">
              <ImagePlus className="w-4 h-4" />
            </button>
          </>
        )}
        <textarea ref={ref} value={value} onChange={handleChange} onKeyDown={onKeyDown} rows={1}
          placeholder={placeholder} maxLength={maxLength} disabled={sending} data-testid={testId}
          className={`flex-1 bg-[#18181b] border border-zinc-700/80 focus:border-amber-500 rounded-lg px-3 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500 resize-none font-sans ${compact ? 'py-1.5 min-h-[36px]' : 'py-2.5 min-h-[44px]'} max-h-[120px]`} />
        {onCancel && (
          <button type="button" onClick={onCancel} className="h-9 w-9 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition-colors" title="Cancel" data-testid={`${testId}-cancel`}>
            <X className="w-4 h-4" />
          </button>
        )}
        <button type="button" onClick={submit} disabled={(!value.trim() && !image) || sending} data-testid={`${testId}-send`}
          className="h-9 px-3 flex items-center gap-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-40 disabled:hover:bg-amber-600 text-black text-xs font-headline tracking-wider transition-colors">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {submitLabel && <span className="hidden sm:inline">{submitLabel}</span>}
        </button>
      </div>
      <div className="flex justify-between mt-1 px-1">
        <span className="text-[10px] text-zinc-600 font-typewriter">{compact ? 'Enter to save · Esc to cancel' : `${onUploadImage ? 'Photo · ' : ''}${allowMentions ? '@ to mention · ' : ''}Enter to send · Shift+Enter for a new line`}</span>
        <span className={`text-[10px] font-typewriter ${value.length > maxLength * 0.9 ? 'text-amber-400' : 'text-zinc-600'}`}>{value.length}/{maxLength}</span>
      </div>
    </div>
  );
};

export default Composer;
